import { ClientSideDataGridModel } from "src/components/common/dataGrid/gridModel/ClientSideDataGridModel.tsx";
import { ProductChangeConnectionsApi } from "src/api/generated/erp/productChanges/api/productChangeConnectionsApi.ts";
import { ProductChangeCatalogPartRevisionView } from "src/api/generated/erp/db/types/tables/productChangeCatalogPartRevisionView.ts";
import { integerColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { faLink } from "@fortawesome/pro-regular-svg-icons";
import { FormCrudDataGrid } from "../../../../../common/dataGrid/crud/FormCrudDataGrid.tsx";
import { useFormInput } from "src/components/common/dialogs/formInput/useFormInput.tsx";
import { FormAsyncSelectField } from "src/components/common/forms/fields/FormAsyncSelectField.tsx";
import { FormLazySelectField } from "src/components/common/forms/fields/FormLazySelectField.tsx";
import { requireRule } from "src/components/common/forms/validation.ts";
import { CatalogPartApi } from "src/api/generated/erp/parts/catalogPart/api/catalogPartApi.ts";
import { CatalogPartView } from "src/api/generated/erp/db/types/tables/catalogPartView.ts";
import { concatWithPipe } from "src/utils/strings.tsx";
import { CatalogPartRevisionApi } from "src/api/generated/erp/parts/catalogPart/api/catalogPartRevisionApi.ts";
import { CatalogPartRevisionView } from "src/api/generated/erp/db/types/tables/catalogPartRevisionView.ts";
import { getCatalogPartRevisionStateLabel } from "src/api/generated/erp/db/types/enums/catalogPartRevisionState.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface ProductChangeCatalogPartRevisionsDataGridProps {
	productChangeId: number;
}

export const ProductChangeCatalogPartRevisionsDataGrid = ({
	productChangeId,
}: ProductChangeCatalogPartRevisionsDataGridProps) => {
	const showFormInput = useFormInput();

	return (
		<ClientSideDataGridModel
			gridId={"F2D6B8A08E634D04"}
			initialParams={{}}
			fetchData={() => ProductChangeConnectionsApi.getConnectedCatalogPartRevisions({ productChangeId })}
			getRows={(data) => data}
			getRowId={(row) => row.productChangeCatalogPartRevisionId}
			customFunctionUiActionView={"PRODUCT_CHANGE_CATALOG_PART_REVISIONS"}
			render={({ dataGridProps, refreshData }) => (
				<FormCrudDataGrid<ProductChangeCatalogPartRevisionView>
					columns={[
						textColumn({
							field: "partNo",
							headerName: i18n.t("part_no"),
							width: 160,
						}),
						integerColumn({
							field: "revisionNumber",
							headerName: i18n.t("revision_abbr"),
							width: 80,
						}),
						textColumn({
							field: "catalogPartDescription_1",
							headerName: i18n.t("description"),
							width: 240,
						}),
						textColumn({
							field: "catalogPartCategoryName",
							headerName: i18n.t("category"),
							width: 160,
						}),
					]}
					actionBarComponents={
						<AsyncButton
							icon={faLink}
							label={i18n.t("connect")}
							variant={"contained"}
							onClick={() => handleConnectRevision(refreshData)}
						/>
					}
					remove={{
						type: "enabled",
						action: async ({ items }) => {
							await ProductChangeConnectionsApi.disconnectCatalogPartRevisions({
								productChangeId,
								catalogPartRevisionIds: items.map((r) => r.catalogPartRevisionId),
							});
						},
					}}
					agentContext={{
						key: defineAgentUiContextKey("erp.productChanges.catalogPartRevisions.grid"),
						data: { productChangeId },
						rowFields: [
							"productChangeCatalogPartRevisionId",
							"catalogPartRevisionId",
							"partNo",
							"revisionNumber",
						],
						selectedRowPathSegment: (row) =>
							`${row.partNo}, ${i18n.t("revision_abbr")} ${row.revisionNumber}`,
						selectedRowsPathSegment: (count) => i18n.t("rows_selected_count", { count }),
					}}
					{...dataGridProps}
				/>
			)}
		/>
	);

	async function handleConnectRevision(refreshData: () => Promise<unknown>) {
		interface FormValues {
			catalogPartId: number;
			catalogPartRevisionId: number;
		}

		const result = await showFormInput<FormValues>({
			title: i18n.t("connect"),
			size: "sm",
			defaultValues: {
				catalogPartId: undefined,
				catalogPartRevisionId: undefined,
			},
			content: ({ control, watch, setValue }) => {
				const catalogPartId = watch("catalogPartId");
				return (
					<>
						<FormAsyncSelectField
							control={control}
							name={"catalogPartId"}
							label={i18n.t("from_part")}
							fetchOptions={({ searchQuery, currentSelection }) =>
								CatalogPartApi.getCatalogPartOptions({
									searchQuery: searchQuery,
									currentSelection: currentSelection,
								})
							}
							getOptionKey={(o: CatalogPartView) => o.catalogPartId}
							getOptionLabel={(o) => concatWithPipe(o.partNo, o.description_1, o.description_2)}
							rules={requireRule()}
							onChange={() => {
								setValue("catalogPartRevisionId", null);
							}}
							autoFocus
						/>
						<FormLazySelectField
							control={control}
							name={"catalogPartRevisionId"}
							label={i18n.t("revision")}
							disabled={catalogPartId == null}
							fetchOptions={async () => {
								if (catalogPartId == null) return [];
								return CatalogPartRevisionApi.getCatalogPartRevisions({
									catalogPartId: catalogPartId,
								});
							}}
							getOptionKey={(o: CatalogPartRevisionView) => o.catalogPartRevisionId}
							getOptionLabel={(o) =>
								concatWithPipe(o.revisionNumber, getCatalogPartRevisionStateLabel(o.state))
							}
							rules={requireRule()}
						/>
					</>
				);
			},
		});
		if (result?.catalogPartRevisionId == null) return;
		await ProductChangeConnectionsApi.connectCatalogPartRevisions({
			productChangeId,
			catalogPartRevisionIds: [result.catalogPartRevisionId],
		});
		await refreshData();
	}
};
